import type { PermissionItem } from '@/views/roles/types';
import { readonly, Ref, ref, watch } from 'vue';
import { Dictionary, groupBy, keyBy, omit } from 'lodash';
import { PermissionDto } from '@/api/generator';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';

export const usePermissions = (
  grantedPermissions: string[],
  lockedPermissions: Ref<string[]>,
  firstVisibleColLevel: number,
) => {
  const permissionItems = ref<PermissionItem[]>();
  const permissionDict = ref<Dictionary<PermissionItem>>({});
  const groupedPermissions = ref<Dictionary<PermissionItem[]>>({});
  const initialzing = ref(false);

  const categoryIdToGroupIdsMap = ref<Record<string, string[]>>({});

  function initPermissions(permissions: PermissionDto[]) {
    initialzing.value = true;

    if (permissions.length) {
      const extendedPermissionData = ref<PermissionItem[]>([]);

      // First cleanup and extend with additional properties
      extendedPermissionData.value = permissions.map((permission) => ({
        ...omit(permission, '$type'),
        level: 0,
        disabled: true,
        granted: false,
      }));

      // Create dictionary for faster access by id's
      permissionDict.value = keyBy(extendedPermissionData.value, 'id');

      extendedPermissionData.value.forEach((extPerm) => {
        extPerm.level = calculatePermissionLevel(extPerm, 0);
        if (extPerm.level === firstVisibleColLevel) {
          extPerm.disabled = false;
        }
        extPerm.childItems = extPerm.children?.map((child) => permissionDict.value[child]);
      });
      permissionItems.value = [...extendedPermissionData.value];

      categoryIdToGroupIdsMap.value = createCategoryMap(permissionItems.value);
      // Group permission items by group name and filter out levels which are not needed for selection
      groupedPermissions.value = groupBy(
        permissionItems.value.filter(
          (permission) =>
            permission.group &&
            (permission.level > firstVisibleColLevel - 1 || // all with level greater then the specified
              (permission.level > 0 && !permission.childItems?.length)), // or all with out childs
        ),
        'group',
      );

      grantedPermissions.map((permissionId) => grantPermissionById(permissionId, true));

      // Now mark all locked permissions
      lockedPermissions.value.map(lockPermissionById);
    }
    initialzing.value = false;
  }

  function createCategoryMap(permissions: PermissionItem[]) {
    const categoryMap: Record<string, string[]> = {};

    permissions
      .filter((item) => !item.group && item.category?.length)
      .forEach((categoryItem) => {
        if (!categoryMap[categoryItem.category!]) categoryMap[categoryItem.category!] = [];
        categoryMap[categoryItem.category!].push(categoryItem.id!);
      });

    return categoryMap;
  }

  function calculatePermissionLevel(item: PermissionItem | null | undefined, level: number) {
    if (!item) return level;

    if (item.parent) {
      const parentItem = permissionDict.value[item.parent];
      return calculatePermissionLevel(parentItem, level + 1);
    }
    return level;
  }

  function setGranted(item: PermissionItem, grant: boolean) {
    if (!grant && item.granted && (item.isLocked || item.hasLockedChilds)) return;

    item.granted = grant;
  }

  function markParentsAsLocked(item: PermissionItem) {
    if (item.parent) {
      const parent = permissionDict.value[item.parent];
      if (parent) {
        if (parent.granted) parent.hasLockedChilds = true;
        markParentsAsLocked(parent);
      }
    }
  }

  function clearPermissionLocks(permissions: PermissionItem[] | undefined) {
    permissions?.map((permission) => {
      permission.isLocked = false;
      permission.hasLockedChilds = false;
    });
  }

  function lockPermissionById(id: string | undefined | null) {
    if (id) {
      const permission = permissionDict.value[id];
      if (permission?.granted) {
        permission.isLocked = true;
        markParentsAsLocked(permission);
      }
    }
  }

  function denyPermissionAndDependents(item: PermissionItem) {
    item.childItems?.forEach((child) => {
      child.disabled = true;
      return denyPermissionAndDependents(child);
    });
    setGranted(item, false);
  }

  function enableDependentsOfPermission(item: PermissionItem, enableChilds: boolean) {
    item.childItems?.map((child) => {
      child.disabled = !enableChilds;
    });
  }

  function grantPermissionById(id: string | undefined | null, grant: boolean | undefined) {
    if (id && grant !== undefined) {
      const permission = permissionDict.value[id];
      if (permission) grantPermission(permission, grant);
    }
  }

  function grantPermission(permission: PermissionItem, grant: boolean) {
    setGranted(permission, grant);
    if (grant) enableDependentsOfPermission(permission, true);
    else denyPermissionAndDependents(permission);
  }

  function devideAndCategorizePermissionGroups(
    category: string,
    categories: Record<string, string[]>,
    permissionsGroups: Dictionary<PermissionItem[]>,
    parts: number,
  ) {
    const dest: Record<string, PermissionItem[]> = {};
    const groupIds = categories[category];

    if (!groupIds?.length) return;

    groupIds.forEach((grpId) => {
      if (permissionsGroups[grpId]) dest[grpId] = permissionsGroups[grpId];
    });

    const keys = Object.keys(dest);
    const splitted: Array<Dictionary<PermissionItem[]>> = [];

    for (let partIter = 0; partIter < parts; partIter++) {
      splitted.push({});
    }

    keys.forEach((key, index) => {
      const idx = Math.floor(index / Math.ceil(keys.length / parts));
      splitted[idx][key] = permissionsGroups[key];
    });
    return splitted;
  }

  function getTranslationKeyByPermissionId(id: string) {
    return permissionDict.value[id]
      ? permissionDict.value[id].i18n || permissionDict.value[id].id!
      : EMPTY_FIELD_SIGN;
  }

  function getGroupGrantedCount(group: PermissionItem[]) {
    return group.reduce((grantedCount: number, currentValue) => {
      return currentValue.granted ? grantedCount + 1 : grantedCount;
    }, 0);
  }

  function clearAllPermissions() {
    Object.keys(groupedPermissions.value).forEach((groupKey) => {
      const groupToToggle = groupedPermissions.value[groupKey];
      if (groupToToggle) toggleAllPermissionsOfGroup(groupToToggle, 'deny');
    });
  }

  function toggleAllPermissionsOfGroup(
    group: PermissionItem[] | undefined,
    mode?: 'grant' | 'deny' | undefined,
  ) {
    if (!group) {
      clearAllPermissions();
    } else {
      mode = !mode && getGroupGrantedCount(group) < group.length ? 'grant' : 'deny';

      group.forEach((permission) => {
        if (permission.level > 2) permission.disabled = true;

        setGranted(permission, mode !== 'deny');
        if (mode === 'grant') {
          permission.disabled = false;
        }
      });
    }
  }

  function cleanupAndSetLockesPermissions() {
    clearPermissionLocks(permissionItems.value);
    lockedPermissions.value.forEach(lockPermissionById);
  }

  watch(
    () => lockedPermissions.value,
    () => {
      cleanupAndSetLockesPermissions();
    },
    {
      deep: true,
    },
  );

  return {
    initPermissions,
    getTranslationKeyByPermissionId,
    getGroupGrantedCount,
    toggleAllPermissionsOfGroup,
    clearAllPermissions,
    grantPermissionById,
    grantPermission,
    categoryIdToGroupIdsMap,
    devideAndCategorizePermissionGroups,
    initialzing: readonly(initialzing),
    groupedPermissions,
    permissionItems: readonly(permissionItems),
  };
};
